import { SchemaObject } from '@augloop/types-core';
import { SessionInitMessage, Response, MicroSyncMessage, SyncMessage, SessionCloseMessage } from '@augloop/session-protocol';
import { Operation as LogOperation, Log, LogCategory } from '@augloop/logging';
import { Utils, LogCountLimiter } from '../utils';
import { JSOND } from '@augloop/jsond';
import { internalFetch } from '../fetch';
import { NetworkRateController } from './network-rate-controller';
import { NetworkMode } from '@augloop/session-protocol-internal';
const minTimeToWaitBeforeNextReconnectTryInMs = 1000;
const offlineConnectionCheckIntervalInMs = 60 * 1000;
// Exported only for unit testing purposes
export const calculateTimeToWaitForNextTry = (attempt, lastAttemptTimeMs, httpTestsSuccessCount) => {
    let result = 0;
    const timeSinceLastConnectMs = Utils.getCurrentTimeMs() ? Utils.getCurrentTimeMs() - lastAttemptTimeMs : 0;
    const timeoutConstantsBasedOnAttempt = [1000, 2000, 5000, 10000, 60000];
    result = timeoutConstantsBasedOnAttempt[Math.max(0, Math.min(timeoutConstantsBasedOnAttempt.length - 1, attempt - 1))];
    result = Math.max(result - timeSinceLastConnectMs, minTimeToWaitBeforeNextReconnectTryInMs);
    // If we have successful HTTP tests, just wait 5 seconds to retry
    result = httpTestsSuccessCount && httpTestsSuccessCount > 0 ? Math.min(5000, result) : result;
    return result;
};
/**
 * Managing websocket workers (websocket connections) in order to create stable connection with successfully initialized session on server
 */
export class NetworkWorkerManager {
    constructor(globalUrl, clientMetadata, workerFactory, sessionInitializer, ingress, queryEgressCacheSize, onConnectionClose, onDetectedWSBlock, settings, networkMode, networkRateSettings) {
        this.logCountLimiter = new LogCountLimiter(NetworkWorkerManager.className);
        this.isWorkerReady = false;
        this.permanentlyClosed = false;
        // Retrying related data
        this.currentReconnectAttempt = 0;
        this.lastInitializationAttemptTimeMs = 0;
        this.offlineInterval = undefined;
        this.alreadyLoggedReconnectAttempt = false;
        this.httpTestsLeft = 5; // number of https health check requests left. Resets after successful connection or on offline mode leave
        this.httpTestsSuccessCount = 0;
        this.workerOpenCount = 0;
        /**
         * Test for an Http Connection when websockets have failed.
         * Because we expect Http and WebSockets to fail together, this will set logOp.dimension1:
         *     'HttpNoResp': both websocket and http response fails, expected error condition when user is offline.
         *     'HttpOK': http succeeded and web sockets are probably blocked.
         *     'HttpErr': an exception (returns .error)
         * In offline mode, this is called on a timer and no logOperation is already in use,
         * so we'll create and start a new one.
        */
        this.testHttpConnection = (logOperation) => {
            const httpUrl = Utils.convertWebSocketUrlToHttp(this.globalUrl);
            const isStandAlone = (!logOperation);
            const logOp = logOperation !== null && logOperation !== void 0 ? logOperation : new LogOperation({
                operationName: 'HttpsTest',
                resourceId: httpUrl,
                success: false,
                resultDescription: '',
                resultSignature: 'HttpResponse:'
            }).start();
            const options = {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    'X-CorrelationId': logOp.cv
                },
                body: JSON.stringify(Utils.createHealthCheckRequest(this.clientMetadata))
            };
            internalFetch(httpUrl, options, (error, response) => {
                if (error) {
                    logOp.dimension1 = 'HttpErr';
                    logOp.resultDescription += `HttpErr: ${error.message}`;
                }
                else if (!response || !response.ok) {
                    logOp.dimension1 = 'HttpNoResp';
                    logOp.resultDescription += `HttpStatus: ${response === null || response === void 0 ? void 0 : response.status}`;
                }
                else {
                    logOp.dimension1 = 'HttpOK';
                    this.leaveOfflineMode();
                    this.httpTestsSuccessCount++;
                    if (isStandAlone) {
                        // We only log http success when the test is run
                        // independent from the WS connection (no logOp was passed in)
                        logOp.success = true;
                    }
                }
                if (isStandAlone) {
                    logOp.stop();
                }
                this.logCountLimiter.log(() => {
                    Log.info(0x1e545704 /* tag_4vf2e */, LogCategory.CoreDefault, logOp);
                });
            });
        };
        this.globalUrl = globalUrl;
        this.clientMetadata = clientMetadata;
        this.workerFactory = workerFactory;
        this.sessionInitializer = sessionInitializer;
        this.ingress = ingress;
        this.queryEgressCacheSize = queryEgressCacheSize;
        this.onConnectionClose = onConnectionClose;
        this.onDetectedWSBlock = onDetectedWSBlock;
        this.closeWSBlockedSession = settings.closeWSBlockedSessionEnabled;
        this.sessionOfflineModeEnabled = settings.sessionOfflineModeEnabled;
        this.httpFallbackEnabled = settings.httpFallbackEnabled;
        this.jsond2ContentTypeEnabled = settings.jsond2ContentTypeEnabled;
        this.closeWorkerOnReliabilityFailureEnabled = settings.closeWorkerOnReliabilityFailureEnabled;
        this.networkWorkerLogOp = new LogOperation({ operationName: 'CreateNetworkWorker', success: true });
        this.networkMode = networkMode ? networkMode : NetworkMode.JSWebSockets;
        this.networkRateSettings = networkRateSettings;
    }
    init(extensionConfigs, customInitPromise, initialAttempt = false) {
        if (this.permanentlyClosed) {
            return Promise.reject(new Error('permanentlyClosed'));
        }
        if (this.isWorkerReady || this.pendingInitPromise) {
            if (this.pendingInitPromise) {
                return this.pendingInitPromise;
            }
            else {
                return Promise.resolve();
            }
        }
        this.extensionConfigs = extensionConfigs || this.extensionConfigs;
        const connectionFailingOrSlowLogSetup = () => {
            if (initialAttempt || !this.alreadyLoggedReconnectAttempt) {
                const timeoutLength = initialAttempt ? NetworkWorkerManager.initialAttemptTimeout : NetworkWorkerManager.reconnectAttemptTimeout;
                this.connTimeout = setTimeout(() => {
                    const connLogOp = new LogOperation({
                        operationName: 'ConnectionFailingOrSlow',
                        dimension0: initialAttempt ? 'Initial' : 'Reconnect',
                        dimension1: timeoutLength.toString(),
                        dimension2: this.currentReconnectAttempt.toString()
                    });
                    Log.info(0x1e54570b /* tag_4vf2l */, LogCategory.CoreDefault, connLogOp);
                    this.alreadyLoggedReconnectAttempt = !initialAttempt;
                    this.connTimeout = undefined;
                }, timeoutLength);
            }
        };
        if (customInitPromise) {
            this.pendingInitPromise = customInitPromise()
                .catch((error) => {
                const logOp = new LogOperation({ operationName: 'WorkerManagerCustomInit', success: false, resultDescription: `${error}` });
                Log.error(0x1e54570a /* tag_4vf2k */, LogCategory.CoreDefault, logOp);
            })
                .then(() => {
                // Initialize regardless of the custom init promise failure to make sure we don't
                // block the AL runtime initialization on custom code.
                connectionFailingOrSlowLogSetup();
                this.initInternal();
            });
        }
        else {
            this.pendingInitPromise = Promise.resolve();
            connectionFailingOrSlowLogSetup();
            this.initInternal();
        }
        return this.pendingInitPromise;
    }
    /**
     * Instruct worker manager to send message to service.
     * It is expected to have ready worker to sent message.
     * @param output object to send to service
     */
    egress(output) {
        if (!this.isWorkerReady && this.pendingInitPromise) {
            return this.pendingInitPromise.then(() => {
                this.egressInternal(output);
            });
        }
        this.egressInternal(output);
        return Promise.resolve();
    }
    /**
     * Instruct worker manager to send message to service.
     * It is expected to have ready worker to sent message.
     * @param bytes bytes to send to service
     */
    egressBytes(bytes) {
        if (!this.isWorkerReady && this.pendingInitPromise) {
            return this.pendingInitPromise.then(() => {
                this.egressBytesInternal(bytes);
            });
        }
        this.egressBytesInternal(bytes);
        return Promise.resolve();
    }
    getNetworkMode() {
        return this.networkMode;
    }
    /**
     * Close current network worker
     */
    close(permanentlyStopConnecting) {
        // Set isWorkerReady first, in case of context switches in worker.close()
        this.isWorkerReady = false;
        if (this.worker) {
            this.worker.close();
            this.worker = null;
        }
        // Close the rate controller only if it exists
        if (this.networkRateController) {
            this.getNetworkRateController().close();
        }
        this.networkRateController = null;
        this.permanentlyClosed = permanentlyStopConnecting || this.permanentlyClosed;
        if (this.permanentlyClosed) {
            this.leaveOfflineMode(); // no need to have offline mode interval if session is permanently closed
            clearTimeout(this.connTimeout);
        }
    }
    initInternal() {
        this.isWorkerReady = false;
        if (this.permanentlyClosed || this.isOffline()) {
            const logOp = new LogOperation({ operationName: 'WorkerManagerInitOffline', success: true, resultSignature: this.permanentlyClosed ? 'PermanentlyClosed' : 'Offline' });
            Log.info(0x1e545709 /* tag_4vf2j */, LogCategory.CoreDefault, logOp);
            return;
        }
        if (this.currentReconnectAttempt > 0) {
            setTimeout(() => {
                this.tryToConnectAndInitializeSession();
            }, calculateTimeToWaitForNextTry(this.currentReconnectAttempt, this.lastInitializationAttemptTimeMs, this.httpFallbackEnabled ? this.httpTestsSuccessCount : undefined));
        }
        else {
            this.tryToConnectAndInitializeSession();
        }
    }
    egressInternal(output) {
        if (!this.isWorkerReady && !SchemaObject.matchesTypesFor(output, [SessionInitMessage.getTypeName()])) {
            if (!SchemaObject.matchesTypesFor(output, [Response.getTypeName()])) {
                Log.error(0x1e545708 /* tag_4vf2i */, LogCategory.CoreDefault, new LogOperation({ operationName: 'UnexpectedEgressCall', resultDescription: `isWorkerReady: ${this.isWorkerReady}` }));
            }
            return;
        }
        let serializedMessage;
        if (SchemaObject.matchesTypesFor(output, [MicroSyncMessage.getTypeName()])) {
            this.castBinaryData(output);
            serializedMessage = JSOND.serialize(output);
        }
        else {
            serializedMessage = JSON.stringify(output);
        }
        // Get sequence number of sync message for validation
        let seq;
        if (SyncMessage.typeGuard(output)) {
            seq = output.seq;
        }
        if (this.bypassRateController(output)) {
            this.worker.egress({ obj: serializedMessage });
            return;
        }
        const isHttpSessionInitMessage = (SessionInitMessage.typeGuard(output) && this.getNetworkMode() === NetworkMode.HttpFallback);
        this.getNetworkRateController().egress({ obj: serializedMessage, isHttpSessionInitMessage: isHttpSessionInitMessage, messageId: output.messageId, seqId: seq });
    }
    bypassRateController(message) {
        return SessionCloseMessage.typeGuard(message);
    }
    egressBytesInternal(bytes) {
        if (!this.isWorkerReady) {
            Log.error(0x1e545707 /* tag_4vf2h */, LogCategory.CoreDefault, new LogOperation({ operationName: 'UnexpectedEgressBytesCall', resultDescription: `isWorkerReady: ${this.isWorkerReady}` }));
            return;
        }
        this.getNetworkRateController().egress({ obj: bytes });
    }
    /**
     * Tries to open connection and initialize session on server.
     * In case of success declare connection ready for using.
     * In case of failure new reconnect attempt will be arranged and retried until success.
     */
    tryToConnectAndInitializeSession() {
        this.currentReconnectAttempt++;
        this.lastInitializationAttemptTimeMs = Utils.getCurrentTimeMs();
        const connectAndInitSession = (logOp) => {
            if (this.permanentlyClosed || this.isOffline()) {
                return;
            }
            // Existence of sliceUrl means that next reconnect should be tried on slice url
            this.connect(this.sliceUrl ? this.sliceUrl : this.globalUrl);
            this.sessionInitializer.initSession({
                isTokenRefresh: false,
                isReconnectOnSameSlice: !!this.sliceUrl,
                extensionConfigs: this.extensionConfigs,
                onResponse: (error, response) => {
                    logOp.success = !error;
                    logOp.resourceId = response ? response.sliceUrl : '';
                    logOp.dimension2 = this.getNetworkModeLogString();
                    if (this.getNetworkMode() === NetworkMode.HttpFallback) {
                        logOp.resultDescription = (error) ? `HTTP Error: ${error.error}` : '';
                        logOp.dimension0 = logOp.success ? 'HTTPOK' : 'HTTPFail';
                    }
                    else {
                        logOp.resultDescription = (error) ? `WS Error: ${error.error}` : '';
                        logOp.dimension0 = logOp.success ? 'WSOK' : 'WSFail';
                    }
                    if (error || !this.worker) {
                        this.sliceUrl = undefined;
                        this.close();
                        if (this.httpTestsLeft > 0) {
                            this.httpTestsLeft--;
                            this.testHttpConnection(logOp.stop());
                        }
                        else {
                            if (this.httpFallbackEnabled) {
                                if (this.getNetworkMode() === NetworkMode.JSWebSockets) {
                                    if (this.workerOpenCount === 0 && this.httpTestsSuccessCount >= 5) {
                                        logOp.dimension1 = 'WSBlocked';
                                        logOp.dimension3 = 'HTTP Fallback';
                                        this.networkMode = NetworkMode.HttpFallback;
                                        this.currentReconnectAttempt = 0;
                                        this.resetHttpTestsCounter();
                                    }
                                    else if (this.sessionOfflineModeEnabled && this.httpTestsSuccessCount === 0) {
                                        this.startOfflineMode();
                                        logOp.dimension1 = 'WSOffline';
                                    }
                                }
                                else if (this.sessionOfflineModeEnabled && this.httpTestsSuccessCount === 0) {
                                    this.startOfflineMode();
                                    logOp.dimension1 = 'HTTPOffline';
                                }
                            }
                            else {
                                // Original code path without httpFallback enabled
                                if (this.closeWSBlockedSession && this.workerOpenCount === 0 && this.httpTestsSuccessCount >= 5) {
                                    this.permanentlyClosed = true;
                                    logOp.dimension1 = 'WSBlocked';
                                    logOp.dimension3 = 'CloseWSBlockedSession';
                                    this.onDetectedWSBlock();
                                }
                                else if (this.sessionOfflineModeEnabled && this.httpTestsSuccessCount === 0) {
                                    this.startOfflineMode();
                                    logOp.dimension1 = 'WSOffline';
                                }
                            }
                            this.logCountLimiter.log(() => {
                                Log.info(0x1e545706 /* tag_4vf2g */, LogCategory.CoreDefault, logOp.stop());
                            });
                        }
                        this.initInternal();
                        return;
                    }
                    else {
                        this.logCountLimiter.log(() => {
                            Log.info(0x1e545705 /* tag_4vf2f */, LogCategory.CoreDefault, logOp.stop());
                        });
                        if (response.forceReconnect) {
                            this.close();
                            const logOp = new LogOperation({ operationName: 'ForcedReconnect' }).start();
                            logOp.resultSignature = `globalUrl: ${this.globalUrl}. sliceUrl: ${this.sliceUrl}`;
                            this.sliceUrl = undefined;
                            setTimeout(() => {
                                connectAndInitSession(logOp);
                            }, minTimeToWaitBeforeNextReconnectTryInMs);
                        }
                        else {
                            clearTimeout(this.connTimeout);
                            this.sliceUrl = response.sliceUrl;
                            this.ready();
                        }
                    }
                    this.resetHttpTestsCounter();
                }
            });
        };
        const logOp = new LogOperation({
            operationName: 'TryToConnectAndInitializeSession',
            resultSignature: `Reconnection attempt #${this.currentReconnectAttempt}`
        }).start();
        connectAndInitSession(logOp);
    }
    getNetworkRateController() {
        if (!this.networkRateController) {
            this.networkRateController = new NetworkRateController(this.networkRateSettings, this.closeWorkerOnReliabilityFailureEnabled);
        }
        return this.networkRateController;
    }
    /**
     * Create network worker with connection to specified url
     * @param url Url address to make connection with
     */
    connect(url) {
        const networkMode = this.getNetworkMode();
        this.networkWorkerLogOp.start();
        this.networkWorkerLogOp.resultDescription = this.getNetworkModeLogString();
        this.networkWorkerLogOp.dimension0 = networkMode.toString();
        this.logCountLimiter.log(() => Log.info(0x1e4d25c2 /* tag_4tsxc */, LogCategory.CoreDefault, this.networkWorkerLogOp.stop()));
        this.worker = this.workerFactory(networkMode);
        this.worker.init(url, this.getNetworkRateController().ingressFromWorker.bind(this.networkRateController), () => {
            this.workerOpenCount++;
            this.getNetworkRateController().open();
            this.leaveOfflineMode();
        }, (error) => {
            // Close the worker manager before notifying listeners
            this.close();
            this.onConnectionClose(error);
        }, this.jsond2ContentTypeEnabled);
        this.getNetworkRateController().init(this.worker, this.ingress, this.queryEgressCacheSize, networkMode, this.clientMetadata);
    }
    /**
     * Declare a current network worker ready.
     */
    ready() {
        this.isWorkerReady = true;
        this.pendingInitPromise = undefined;
        this.currentReconnectAttempt = 0;
    }
    /**
     * Cast object's binary fields to Uint8Array so
     * JSOND can serialize it appropriately
     * @param obj Object whose binary data is to be cast to Uint8Array
     */
    castBinaryData(obj) {
        if (!obj) {
            return;
        }
        if (Array.isArray(obj.__binaryMembers__)) {
            for (const member of obj.__binaryMembers__) {
                if (!ArrayBuffer.isView(obj[member])) {
                    obj[member] = new Uint8Array(obj[member]);
                }
            }
        }
        for (const key of Object.keys(obj)) {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                this.castBinaryData(obj[key]);
            }
        }
    }
    isOffline() {
        return !!this.offlineInterval;
    }
    startOfflineMode() {
        this.offlineInterval = setInterval(() => {
            this.testHttpConnection();
        }, offlineConnectionCheckIntervalInMs);
    }
    leaveOfflineMode() {
        if (!this.isOffline()) {
            return;
        }
        clearInterval(this.offlineInterval);
        this.offlineInterval = undefined;
        this.resetHttpTestsCounter();
        this.tryToConnectAndInitializeSession();
    }
    resetHttpTestsCounter() {
        this.httpTestsLeft = 5;
        this.httpTestsSuccessCount = 0;
    }
    getNetworkModeLogString() {
        return 'NetworkMode: ' + (this.getNetworkMode() === NetworkMode.JSWebSockets ? 'WebSocket' : 'HTTP');
    }
}
// Only for logging purposes
NetworkWorkerManager.initialAttemptTimeout = 100000;
NetworkWorkerManager.reconnectAttemptTimeout = 20000;
NetworkWorkerManager.className = 'NetworkWorkerManager';
//# sourceMappingURL=network-worker-manager.js.map